/*
 * Predictive search — original typing/suggest behavior PLUS a curated "focus panel"
 * that opens when the shopper focuses/clicks the search box while it is EMPTY.
 *
 * The focus panel markup is rendered server-side by snippets/search-focus-panel.liquid
 * (element [data-search-focus-panel]). This script only toggles its visibility; it never
 * fetches anything for the panel. Typing still drives the normal predictive results exactly
 * as before — the panel is hidden the moment a search term exists and reappears when cleared.
 */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('#predictive-search');
    this.focusPanel = this.querySelector('[data-search-focus-panel]');
    this.productStrip = this.querySelector('[data-focus-products]');
    this.slidePrev = this.querySelector('[data-focus-slide="prev"]');
    this.slideNext = this.querySelector('[data-focus-slide="next"]');
    this._blurTimer = null;
    this._lastPanelPointerAt = 0;
    this._lastPointerDownAt = 0;
    this._lastPointerDownInside = false;
    this._lastViewportChangeAt = 0;

    this.input.addEventListener(
      'input',
      this.debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );

    // --- Focus panel wiring (only if the curated panel exists) ---
    if (this.focusPanel) {
      // Open on focus/click when the field is empty.
      this.input.addEventListener('focus', this.onInputFocus.bind(this));
      this.input.addEventListener('click', this.onInputFocus.bind(this));

      // Immediate (non-debounced) toggle so the panel/results swap feels instant
      // as the user types or clears the field. The debounced onChange still runs
      // the actual predictive fetch.
      this.input.addEventListener('input', this.onImmediateInput.bind(this));

      // Close on Escape, outside click, and blur (with a small delay so a click on
      // a chip / product inside the panel registers before it closes).
      this.input.addEventListener('keydown', this.onKeyDown.bind(this));
      this.input.addEventListener('blur', this.onInputBlur.bind(this));
      document.addEventListener('click', this.onDocumentClick.bind(this));

      // Interacting with the panel (tapping a chip, the slide buttons, empty
      // space, or dragging the product strip) blurs the input, which would
      // otherwise trigger the blur-close. Track pointer interactions that start
      // inside the panel and suppress the blur-close for them. pointer events
      // cover mouse + touch + pen uniformly, so this works on mobile too.
      this._pointerInPanel = false;
      this.focusPanel.addEventListener('pointerdown', this.onPanelPointerDown.bind(this), {
        capture: true,
      });
      this.focusPanel.addEventListener('mousedown', this.onPanelPointerDown.bind(this), true);
      this.focusPanel.addEventListener('touchstart', this.onPanelPointerDown.bind(this), {
        capture: true,
        passive: true,
      });
      this.focusPanel.addEventListener('click', this.onFocusChipClick.bind(this));
      document.addEventListener('pointerdown', this.onDocumentPointerDown.bind(this), true);
      document.addEventListener('mousedown', this.onDocumentPointerDown.bind(this), true);
      document.addEventListener('touchstart', this.onDocumentPointerDown.bind(this), {
        capture: true,
        passive: true,
      });
      // Clear the flag after the resulting click has been handled. Runs after
      // the click cycle so onInputBlur (which fires before the click) can read it.
      document.addEventListener('pointerup', this.clearPanelPointer.bind(this));
      document.addEventListener('pointercancel', this.clearPanelPointer.bind(this));
      document.addEventListener('mouseup', this.clearPanelPointer.bind(this));
      document.addEventListener('touchend', this.clearPanelPointer.bind(this), { passive: true });
      document.addEventListener('touchcancel', this.clearPanelPointer.bind(this), { passive: true });

      // Close the panel when the PAGE scrolls (the dropdown would otherwise
      // detach from the search box). Scrolling INSIDE the panel does not fire a
      // window scroll event, so the panel's own inner scroll stays unaffected.
      this._onWindowScroll = this.onWindowScroll.bind(this);
      window.addEventListener('scroll', this._onWindowScroll, { passive: true });
      this._onViewportChange = this.onViewportChange.bind(this);
      window.addEventListener('resize', this._onViewportChange, { passive: true });
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this._onViewportChange, { passive: true });
        window.visualViewport.addEventListener('scroll', this._onViewportChange, { passive: true });
      }

      // --- Mobile product-strip slide arrows ---
      if (this.productStrip && (this.slidePrev || this.slideNext)) {
        if (this.slidePrev) {
          this.slidePrev.addEventListener('click', (event) => {
            event.preventDefault();
            this.slide(-1);
          });
        }
        if (this.slideNext) {
          this.slideNext.addEventListener('click', (event) => {
            event.preventDefault();
            this.slide(1);
          });
        }
        // Keep arrow enabled/disabled states in sync with the scroll position.
        this.productStrip.addEventListener(
          'scroll',
          this.debounce(() => this.updateSlideButtons(), 80).bind(this),
          { passive: true }
        );
      }
    }
  }

  /* Scroll the product strip by roughly one viewport of cards. */
  slide(direction) {
    if (!this.productStrip) return;
    this.onPanelPointerDown();
    this.clearPanelPointer();
    this.openFocusPanel();
    const el = this.productStrip;
    const maxScroll = this.getProductStripMaxScroll();
    const positions = this.getProductCardPositions();
    const current = el.scrollLeft;
    let target = direction > 0 ? maxScroll : 0;

    if (direction > 0) {
      target = positions.find((position) => position > current + 2) ?? maxScroll;
    } else {
      target = [...positions].reverse().find((position) => position < current - 2) ?? 0;
    }

    target = Math.max(0, Math.min(target, maxScroll));
    if (Math.abs(target - current) < 1) {
      this.updateSlideButtons();
      return;
    }

    el.scrollTo({ left: target, behavior: 'smooth' });
    window.requestAnimationFrame(() => this.updateSlideButtons());
    setTimeout(() => this.updateSlideButtons(), 350);
  }

  /* Disable the prev/next arrow when the strip can't scroll further that way. */
  updateSlideButtons() {
    if (!this.productStrip) return;
    const el = this.productStrip;
    const maxScroll = this.getProductStripMaxScroll();
    const atStart = el.scrollLeft <= 1;
    const atEnd = el.scrollLeft >= maxScroll - 1;
    const scrollable = maxScroll > 2;
    if (this.slidePrev) this.slidePrev.disabled = !scrollable || atStart;
    if (this.slideNext) this.slideNext.disabled = !scrollable || atEnd;
  }

  getProductCardPositions() {
    if (!this.productStrip) return [];
    const stripRect = this.productStrip.getBoundingClientRect();
    return Array.from(this.productStrip.querySelectorAll('.sfp-card')).map((card) =>
      Math.round(card.getBoundingClientRect().left - stripRect.left + this.productStrip.scrollLeft)
    );
  }

  getProductStripMaxScroll() {
    if (!this.productStrip) return 0;
    const lastCard = this.productStrip.querySelector('.sfp-card:last-child');
    if (!lastCard) {
      return Math.max(0, this.productStrip.scrollWidth - this.productStrip.clientWidth);
    }

    const stripRect = this.productStrip.getBoundingClientRect();
    const lastCardRight =
      lastCard.getBoundingClientRect().right - stripRect.left + this.productStrip.scrollLeft;
    return Math.max(0, Math.ceil(lastCardRight - this.productStrip.clientWidth));
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    fetch(
      `/search/suggest?q=${searchTerm}&resources[type]=product&resources[limit]=4&section_id=predictive-search`
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.open();
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  open() {
    // Predictive results are showing -> the curated focus panel must be hidden.
    this.closeFocusPanel();
    this.predictiveSearchResults.style.display = 'block';
  }

  close() {
    this.predictiveSearchResults.style.display = 'none';
    // If the field is empty and still focused, show the curated panel instead.
    if (this.focusPanel && !this.input.value.trim().length && document.activeElement === this.input) {
      this.openFocusPanel();
    }
  }

  /* ---------- Focus panel ---------- */

  onInputFocus() {
    if (!this.input.value.trim().length) {
      this.openFocusPanel();
    }
  }

  onImmediateInput() {
    if (this.input.value.trim().length) {
      // A term exists — hide the curated panel; predictive results take over.
      this.closeFocusPanel();
    } else {
      // Field cleared again — bring the curated panel back.
      this.predictiveSearchResults.style.display = 'none';
      this.openFocusPanel();
    }
  }

  onFocusChipClick(event) {
    const chip = event.target.closest('[data-focus-chip]');
    if (!chip || !this.focusPanel.contains(chip)) return;

    event.preventDefault();

    // Fill the field and immediately switch from the curated panel to predictive
    // product suggestions. This does not submit or navigate to the search page.
    const searchTerm = chip.dataset.searchTerm || '';
    this.input.value = searchTerm;
    this.input.focus({ preventScroll: true });
    this.input.setSelectionRange(searchTerm.length, searchTerm.length);
    this.closeFocusPanel();
    this.predictiveSearchResults.style.display = 'none';
    this.getSearchResults(searchTerm);
  }

  onKeyDown(event) {
    if (event.key === 'Escape') {
      this.closeFocusPanel();
      this.predictiveSearchResults.style.display = 'none';
      this.input.blur();
    }
  }

  onInputBlur(event) {
    // If the blur was caused by interacting with the panel itself (a chip, the
    // slide buttons, empty space, or dragging the product strip), do NOT close —
    // the interaction should keep the panel open. onDocumentClick handles the
    // real "clicked outside" case.
    if (this._isRecentPanelInteraction()) return;

    if (event.relatedTarget && this.contains(event.relatedTarget)) return;

    const recentPointer = Date.now() - this._lastPointerDownAt < PredictiveSearch.PANEL_INTERACTION_GRACE_MS;

    // On mobile, hiding the software keyboard blurs the input without a real
    // outside tap. Keep the curated panel open; outside taps are still handled
    // by onDocumentClick.
    if (this.isTouchViewport() && (!recentPointer || this._lastPointerDownInside)) return;

    // Delay so a click that moves focus elsewhere in our own component (e.g. the
    // submit button) still registers before we close.
    this._blurTimer = setTimeout(() => {
      if (this._isRecentPanelInteraction()) return;
      if (document.activeElement && this.contains(document.activeElement)) return;
      this.closeFocusPanel();
    }, 200);
  }

  onPanelPointerDown() {
    this._pointerInPanel = true;
    this._lastPanelPointerAt = Date.now();
    if (this._blurTimer) clearTimeout(this._blurTimer);
  }

  clearPanelPointer() {
    // Defer so any click handler / blur that follows the pointer end still sees
    // the panel as "being interacted with".
    setTimeout(() => {
      this._pointerInPanel = false;
    }, PredictiveSearch.PANEL_INTERACTION_GRACE_MS);
  }

  onDocumentPointerDown(event) {
    this._lastPointerDownAt = Date.now();
    this._lastPointerDownInside = this.contains(event.target);
  }

  onViewportChange() {
    this._lastViewportChangeAt = Date.now();
  }

  onDocumentClick(event) {
    // The panel lives inside <predictive-search> (this), so any click within the
    // component — input, chips, product cards, slide buttons — is "inside" and
    // must NOT close the panel. Only a click fully outside closes it.
    if (this.contains(event.target)) return;
    if (this._blurTimer) clearTimeout(this._blurTimer);
    this.closeFocusPanel();
  }

  onWindowScroll() {
    // Only act when a dropdown is actually open, so we don't blur unrelated
    // focus or thrash on every scroll frame.
    const panelOpen = this.focusPanel && !this.focusPanel.hidden;
    const resultsOpen =
      this.predictiveSearchResults &&
      this.predictiveSearchResults.style.display === 'block';
    if (!panelOpen && !resultsOpen) return;

    // Stay open through small scrolls; only close once the user has scrolled a
    // meaningful distance (SCROLL_CLOSE_THRESHOLD px) away from where the panel
    // opened. This tolerates the tiny scroll the mobile keyboard/focus can cause.
    const current = window.pageYOffset || document.documentElement.scrollTop || 0;

    if (this._isRecentPanelInteraction()) {
      this._openScrollY = current;
      return;
    }

    if (
      this.isTouchViewport() &&
      Date.now() - this._lastViewportChangeAt < PredictiveSearch.MOBILE_VIEWPORT_GRACE_MS
    ) {
      this._openScrollY = current;
      return;
    }

    const distance = Math.abs(current - (this._openScrollY || 0));
    if (distance < PredictiveSearch.SCROLL_CLOSE_THRESHOLD) return;

    this.closeFocusPanel();
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.display = 'none';
    }
    // Drop focus so focus/close/focus doesn't immediately reopen the panel.
    if (document.activeElement === this.input) this.input.blur();
  }

  openFocusPanel() {
    if (!this.focusPanel) return;
    if (this._blurTimer) clearTimeout(this._blurTimer);
    // Remember the scroll position at open time — the scroll-to-close threshold
    // is measured relative to this.
    this._openScrollY =
      window.pageYOffset || document.documentElement.scrollTop || 0;
    this.focusPanel.hidden = false;
    this.focusPanel.classList.add('is-open');
    this.input.setAttribute('aria-expanded', 'true');
    // Set the correct initial prev/next arrow states for the product strip.
    this.updateSlideButtons();
  }

  closeFocusPanel() {
    if (!this.focusPanel) return;
    this.focusPanel.hidden = true;
    this.focusPanel.classList.remove('is-open');
    // Only reset aria-expanded if predictive results aren't showing either.
    if (this.predictiveSearchResults && this.predictiveSearchResults.style.display === 'block') return;
    this.input.setAttribute('aria-expanded', 'false');
  }

  _isRecentPanelInteraction() {
    return (
      this._pointerInPanel ||
      Date.now() - this._lastPanelPointerAt < PredictiveSearch.PANEL_INTERACTION_GRACE_MS
    );
  }

  isTouchViewport() {
    return (
      window.innerWidth <= 990 ||
      window.matchMedia('(hover: none), (pointer: coarse)').matches
    );
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

// How far (px) the page must scroll from where the panel opened before it
// closes. Small/medium scrolls (< this) leave the panel open so it doesn't feel
// twitchy; only a deliberate long scroll dismisses it.
PredictiveSearch.SCROLL_CLOSE_THRESHOLD = 400;
PredictiveSearch.PANEL_INTERACTION_GRACE_MS = 650;
PredictiveSearch.MOBILE_VIEWPORT_GRACE_MS = 900;

customElements.define('predictive-search', PredictiveSearch);
